import React, { useContext, useState } from 'react'
import ReactModal from 'react-modal';
import { toast } from 'react-toastify';
import { AiOutlineClose, AiOutlineFilePdf } from 'react-icons/ai';

import Context, { IContext } from '../../context/Context';
import { IReserva } from '../../types/Reserva';
import api from '../../services/api';

interface IModalReservaProps {
  modalVisible: boolean;
  setModalVisible: (visivel: boolean) => void;
  reservaEditando?: IReserva;
  onConfirmado?: () => void;
}

// aceent chega da API como dd/MM/yyyy HH:mm (já com -3h); volta para yyyy-MM-dd HH:mm somando as 3 horas
function converterParaISOComOffset(dataStr: string): string {
  const [dia, mes, anoHora] = dataStr.split('/');
  const [ano, horaMinuto] = anoHora.split(' ');
  const [hora, minuto] = horaMinuto.split(':');

  // Cria o objeto Date assumindo como se fosse no fuso local
  const data = new Date(
    Number(ano),
    Number(mes) - 1,
    Number(dia),
    Number(hora),
    Number(minuto)
  );

  // Soma 3 horas
  data.setHours(data.getHours() + 3);

  // Formata para yyyy-MM-dd HH:mm
  const yyyy = data.getFullYear();
  const MM = String(data.getMonth() + 1).padStart(2, '0');
  const dd = String(data.getDate()).padStart(2, '0');
  const HH = String(data.getHours()).padStart(2, '0');
  const mm = String(data.getMinutes()).padStart(2, '0');

  return `${yyyy}-${MM}-${dd} ${HH}:${mm}`;
}

export default function ModalReserva({ modalVisible, setModalVisible, reservaEditando, onConfirmado }: IModalReservaProps) {
  const { setIsLoadingOverlay, usuario, empresa }: IContext = useContext(Context);

  const [obsInput, setObsInput] = useState(reservaEditando?.obster || '');

  // Quem não é o responsável pela reserva (portaria) está fazendo a conferência de saída
  const ehConferencia = usuario?.cod !== reservaEditando?.usuario.cod;

  async function putReserva(reserva: IReserva) {
    try {
      setIsLoadingOverlay(true);

      const payload = {
        codesp: reserva.espaco.cod,
        datres: reserva.datres,
        codusu: reserva.usuario.cod,
        sta: reserva.sta,
        aceent: reserva.aceent,
        acesai: reserva.acesai,
        val: reserva.val,
        obs: reserva.obs,
        checkout: reserva.checkout,
        obster: reserva.obster,
        codtipres: reserva.tipres.cod
      }

      const response = await api.put(`/reservas/${reserva.cod}`, payload);

      if (response.status === 200) {
        toast.success('Aceite confirmado com sucesso.');
        setModalVisible(false);
        onConfirmado?.();
      }
    } catch (error: any) {
      if (error.response?.data) return toast.error(error.response.data.erro);
      toast.error('Erro ao editar reserva.');
    } finally {
      setTimeout(() => {
        setIsLoadingOverlay(false);
      }, 500);
    }
  }

  function confirmar() {
    if (!reservaEditando) return;

    // Trabalha numa cópia para não marcar a reserva como aceita na lista caso a gravação falhe
    const novaReserva: IReserva = { ...reservaEditando };

    novaReserva.aceent = novaReserva.aceent ? converterParaISOComOffset(novaReserva.aceent) : new Date().toISOString();
    novaReserva.obster = obsInput;

    if (ehConferencia) {
      novaReserva.acesai = new Date().toISOString();
      novaReserva.obster = `${obsInput}. Funcionário: ${usuario?.usu}`;
    }

    putReserva(novaReserva);
  }

  return (
    <ReactModal
      isOpen={modalVisible}
      appElement={document.getElementById('root') as HTMLElement}
      contentLabel={ehConferencia ? 'Conferência da reserva' : 'Aceite do termo'}
      shouldCloseOnOverlayClick={true}
      onRequestClose={() => setModalVisible(false)}
      overlayClassName='fixed inset-0 z-[99] flex items-end md:items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in motion-reduce:animate-none'
      className='relative flex w-full max-h-[92dvh] flex-col overflow-hidden rounded-t-2xl bg-white shadow-card outline-none animate-slide-up motion-reduce:animate-none md:max-w-lg md:rounded-2xl'
    >
      <div className='flex items-start justify-between gap-3 border-b border-zinc-100 px-5 py-4'>
        <div className='min-w-0'>
          <h2 className='text-lg font-semibold text-zinc-900'>{ehConferencia ? 'Conferência' : 'Aceite do Termo'}</h2>
          <p className='mt-0.5 text-sm text-zinc-500'>
            {reservaEditando?.espaco.esp} · {reservaEditando?.datres}
            {ehConferencia && reservaEditando?.usuario.usu ? ` · ${reservaEditando.usuario.usu}` : ''}
          </p>
        </div>
        <button
          type='button'
          onClick={() => setModalVisible(false)}
          aria-label='Fechar'
          className='flex h-10 w-10 flex-none items-center justify-center rounded-full text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900'
        >
          <AiOutlineClose className='h-5 w-5' aria-hidden='true' />
        </button>
      </div>

      <div className='min-h-0 space-y-4 overflow-y-auto px-5 py-5'>
        {!ehConferencia && (
          <p className='text-sm leading-relaxed text-zinc-700'>
            Eu <b className='text-zinc-900'>{reservaEditando?.usuario.usu}</b>, associado(a) da empresa {empresa?.raz || ''}, responsável pela reserva do espaco <b className='text-zinc-900'>{reservaEditando?.espaco.esp}</b> no dia <b className='text-zinc-900'>{reservaEditando?.datres}</b>. Declaro que retirei a título de empréstimo os utensílios abaixo discriminados, comprometendo-me a devolvê-los em perfeitas condições após o evento.
          </p>
        )}

        {reservaEditando?.espaco.tipesp.det && (
          <div
            className='rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-3 text-sm leading-relaxed text-zinc-700'
            dangerouslySetInnerHTML={{ __html: reservaEditando.espaco.tipesp.det }}
          />
        )}

        <div>
          <label htmlFor='obsTermo' className='mb-1 block text-sm font-medium text-zinc-700'>
            {ehConferencia ? 'Perdas e observação' : 'Observação'}
          </label>
          <textarea
            id='obsTermo'
            rows={4}
            placeholder={ehConferencia ? 'Perdas e Observação' : 'Digite Aqui Qualquer Observação'}
            value={obsInput}
            onChange={(e) => setObsInput(e.target.value)}
            className='w-full resize-y rounded-xl border border-zinc-300 bg-white px-4 py-3 text-zinc-900 placeholder:text-zinc-400 focus:border-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900/10'
          />
        </div>
      </div>

      <div className='space-y-3 border-t border-zinc-100 px-5 pt-4 pb-[max(16px,env(safe-area-inset-bottom))]'>
        <p className='text-sm text-zinc-500'>
          Ao confirmar, estou de acordo com o{' '}
          <a
            href='https://www.afmm.com.br/arquivos/downloads/TERMO-DE-RESPONSABILIDADE.pdf'
            target='_blank'
            rel='noreferrer'
            className='inline-flex items-center gap-1 font-semibold text-zinc-900 underline underline-offset-4 hover:text-zinc-600'
          >
            <AiOutlineFilePdf aria-hidden='true' />
            Termo de Responsabilidade
          </a>.
        </p>
        <button
          type='button'
          onClick={confirmar}
          className='flex h-12 w-full items-center justify-center rounded-xl bg-zinc-900 px-5 font-semibold text-white transition-colors hover:bg-zinc-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 focus-visible:ring-offset-2'
        >
          {ehConferencia ? 'Confirmar Conferência' : 'Estou Ciente'}
        </button>
      </div>
    </ReactModal>
  )
}
